import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
  where,
  writeBatch
} from "firebase/firestore";
import { db } from "./config.js";
import { getDayName, getTodayKey } from "../utils/date.js";
import { calculateLogMetrics } from "../utils/time.js";

export const DEFAULT_SETTINGS = {
  appName: "PARIKSIT Teachers Time Tracker",
  instituteName: "PARIKSIT",
  entryGraceMinutes: 5,
  exitGraceMinutes: 5
};

const ID_FIELDS = {
  teachers: "teacherId",
  subjects: "subjectId",
  classes: "classId",
  sections: "sectionId",
  rooms: "roomId",
  routines: "routineId",
  timeLogs: "logId"
};

const MASTER_COLLECTIONS = ["teachers", "subjects", "classes", "sections", "rooms"];
const MASTER_CACHE_KEY = "pariksit-master-cache-v1";
const MASTER_CACHE_TTL = 1000 * 60 * 20;

let memoryMasterCache = {
  expiresAt: 0,
  data: null
};

function stripUndefined(value) {
  return Object.fromEntries(Object.entries(value).filter(([, item]) => item !== undefined));
}

function snapRows(snapshot) {
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

function activeSort(a, b) {
  const activeCompare = Number(b.active !== false) - Number(a.active !== false);
  if (activeCompare !== 0) return activeCompare;
  return String(a.name || a.roomNumber || "").localeCompare(String(b.name || b.roomNumber || ""));
}

function readLocalMasterCache() {
  try {
    const cached = JSON.parse(localStorage.getItem(MASTER_CACHE_KEY) || "null");
    if (cached?.expiresAt > Date.now()) return cached;
  } catch {
    return null;
  }
  return null;
}

function writeLocalMasterCache(data) {
  try {
    const payload = { data, expiresAt: Date.now() + MASTER_CACHE_TTL };
    localStorage.setItem(MASTER_CACHE_KEY, JSON.stringify(payload));
    memoryMasterCache = payload;
  } catch {
    memoryMasterCache = { data, expiresAt: Date.now() + MASTER_CACHE_TTL };
  }
}

export function clearMasterCache() {
  memoryMasterCache = { expiresAt: 0, data: null };
  try {
    localStorage.removeItem(MASTER_CACHE_KEY);
  } catch {
    // Local cache is optional.
  }
}

export async function getUserProfile(uid) {
  const snapshot = await getDoc(doc(db, "users", uid));
  if (!snapshot.exists()) return null;
  return { uid: snapshot.id, ...snapshot.data() };
}

export async function getUsers() {
  const snapshot = await getDocs(query(collection(db, "users"), orderBy("email", "asc")));
  return snapRows(snapshot).map((user) => ({ uid: user.id, ...user }));
}

export async function saveUserProfile(uid, payload) {
  await setDoc(
    doc(db, "users", uid),
    stripUndefined({
      ...payload,
      uid,
      updatedAt: serverTimestamp()
    }),
    { merge: true }
  );
}

export async function getSettings() {
  const snapshot = await getDoc(doc(db, "settings", "app"));
  return snapshot.exists() ? { ...DEFAULT_SETTINGS, ...snapshot.data() } : DEFAULT_SETTINGS;
}

export async function saveSettings(settings) {
  await setDoc(
    doc(db, "settings", "app"),
    stripUndefined({ ...settings, updatedAt: serverTimestamp() }),
    { merge: true }
  );
}

export async function getCollectionRows(collectionName, includeInactive = true) {
  const snapshot = await getDocs(collection(db, collectionName));
  const rows = snapRows(snapshot);
  return (includeInactive ? rows : rows.filter((row) => row.active !== false)).sort(activeSort);
}

export async function getMasterData(forceRefresh = false) {
  if (!forceRefresh && memoryMasterCache.data && memoryMasterCache.expiresAt > Date.now()) {
    return memoryMasterCache.data;
  }

  if (!forceRefresh) {
    const localCache = readLocalMasterCache();
    if (localCache?.data) {
      memoryMasterCache = localCache;
      return localCache.data;
    }
  }

  const entries = await Promise.all(
    MASTER_COLLECTIONS.map(async (name) => [name, await getCollectionRows(name, true)])
  );
  const data = Object.fromEntries(entries);
  writeLocalMasterCache(data);
  return data;
}

export async function getUserRoutineEditMasterData() {
  const [teachersSnapshot, subjectsSnapshot] = await Promise.all([
    getDocs(query(collection(db, "teachers"), where("active", "==", true))),
    getDocs(query(collection(db, "subjects"), where("active", "==", true)))
  ]);
  const sortByName = (a, b) => String(a.name || "").localeCompare(String(b.name || ""), undefined, { numeric: true });
  return {
    teachers: snapRows(teachersSnapshot).sort(sortByName),
    subjects: snapRows(subjectsSnapshot).sort(sortByName)
  };
}

export async function saveRecord(collectionName, payload, id = null) {
  const ref = id ? doc(db, collectionName, id) : doc(collection(db, collectionName));
  const idField = ID_FIELDS[collectionName] || "id";
  await setDoc(
    ref,
    stripUndefined({
      ...payload,
      [idField]: ref.id,
      active: payload.active ?? true,
      updatedAt: serverTimestamp(),
      ...(!id ? { createdAt: serverTimestamp() } : {})
    }),
    { merge: true }
  );
  clearMasterCache();
  return ref.id;
}

export async function deleteRecord(collectionName, id) {
  await deleteDoc(doc(db, collectionName, id));
  clearMasterCache();
}

export async function updateRecord(collectionName, id, payload) {
  await updateDoc(
    doc(db, collectionName, id),
    stripUndefined({
      ...payload,
      updatedAt: serverTimestamp()
    })
  );
  clearMasterCache();
}

export async function getRoutines() {
  const snapshot = await getDocs(query(collection(db, "routines"), orderBy("order", "asc")));
  return snapRows(snapshot);
}

export async function getTodayRoutines(day = getDayName()) {
  const snapshot = await getDocs(
    query(collection(db, "routines"), where("active", "==", true))
  );
  return snapRows(snapshot)
    .filter((row) => Array.isArray(row.days) && row.days.includes(day))
    .sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
}

export function listenTodayRoutines(day = getDayName(), callback) {
  return onSnapshot(
    query(collection(db, "routines"), where("active", "==", true)),
    (snapshot) => {
      callback(
        snapRows(snapshot)
          .filter((row) => Array.isArray(row.days) && row.days.includes(day))
          .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
      );
    }
  );
}

export async function saveRoutine(payload, id = null) {
  const ref = id ? doc(db, "routines", id) : doc(collection(db, "routines"));
  await setDoc(
    ref,
    stripUndefined({
      ...payload,
      routineId: ref.id,
      active: payload.active ?? true,
      order: Number(payload.order ?? Date.now()),
      updatedAt: serverTimestamp(),
      ...(!id ? { createdAt: serverTimestamp() } : {})
    }),
    { merge: true }
  );
  return ref.id;
}

export async function userUpdateRoutine(routine, patch, user) {
  const routineId = routine?.id || routine?.routineId;
  if (!routineId) throw new Error("Routine not found");
  await setDoc(
    doc(db, "routines", routineId),
    stripUndefined({
      ...patch,
      routineId,
      updatedBy: user?.uid || user?.id || "",
      updatedByName: user?.name || user?.email || "",
      updatedAt: serverTimestamp()
    }),
    { merge: true }
  );
}

export async function deleteRoutine(id) {
  await deleteDoc(doc(db, "routines", id));
}

export async function duplicateRoutine(routine) {
  const copy = { ...routine };
  delete copy.id;
  delete copy.routineId;
  delete copy.createdAt;
  delete copy.updatedAt;
  return saveRoutine({
    ...copy,
    notes: copy.notes ? `${copy.notes} (duplicate)` : "Duplicated routine",
    order: Number(copy.order || Date.now()) + 1
  });
}

export async function reorderRoutines(routines) {
  const batch = writeBatch(db);
  routines.forEach((routine, index) => {
    batch.update(doc(db, "routines", routine.id), {
      order: index + 1,
      updatedAt: serverTimestamp()
    });
  });
  await batch.commit();
}

export async function copyRoutinesBetweenDays(sourceDay, targetDay) {
  const snapshot = await getDocs(
    query(collection(db, "routines"), where("active", "==", true))
  );
  const batch = writeBatch(db);
  snapshot.docs.filter((item) => Array.isArray(item.data().days) && item.data().days.includes(sourceDay)).forEach((item, index) => {
    const data = item.data();
    const ref = doc(collection(db, "routines"));
    batch.set(ref, {
      ...data,
      routineId: ref.id,
      days: [targetDay],
      order: Date.now() + index,
      notes: data.notes ? `${data.notes} | copied from ${sourceDay}` : `Copied from ${sourceDay}`,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  });
  await batch.commit();
  return snapshot.size;
}

export async function importRoutines(rows) {
  const batch = writeBatch(db);
  rows.forEach((row, index) => {
    const ref = doc(collection(db, "routines"));
    const days = String(row.Day || row.days || "")
      .split(",")
      .map((day) => day.trim())
      .filter(Boolean);
    batch.set(ref, {
      routineId: ref.id,
      days,
      roomNumber: row.Room || row.roomNumber || "",
      className: row.Class || row.className || "",
      sectionName: row.Section || row.sectionName || "",
      subjectName: row.Subject || row.subjectName || "",
      teacherName: row.Teacher || row.teacherName || "",
      scheduledIn: row["Schedule IN"] || row.scheduledIn || "",
      scheduledOut: row["Schedule OUT"] || row.scheduledOut || "",
      status: row.Status || row.status || "active",
      active: String(row.Active ?? "true").toLowerCase() !== "false",
      notes: row.Notes || row.notes || "",
      order: Date.now() + index,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  });
  await batch.commit();
}

export async function getTodayLogs(date = getTodayKey()) {
  const snapshot = await getDocs(query(collection(db, "timeLogs"), where("date", "==", date)));
  return snapRows(snapshot);
}

export function listenTodayLogs(date, callback) {
  return onSnapshot(query(collection(db, "timeLogs"), where("date", "==", date)), (snapshot) => {
    callback(snapRows(snapshot));
  });
}

export function listenTodayQueueLogs(date, callback) {
  return onSnapshot(
    query(
      collection(db, "timeLogs"),
      where("date", "==", date)
    ),
    (snapshot) => {
      callback(snapRows(snapshot).filter((log) => ["pendingOut", "suspended"].includes(log.userQueueStatus)));
    }
  );
}

function routineLogBase(routine, date, day) {
  return {
    date,
    day,
    logDate: new Date(`${date}T00:00:00`),
    routineId: routine.id || routine.routineId,
    teacherId: routine.teacherId || "",
    subjectId: routine.subjectId || "",
    classId: routine.classId || "",
    sectionId: routine.sectionId || "",
    roomId: routine.roomId || "",
    teacherName: routine.teacherName || "",
    subjectName: routine.subjectName || "",
    className: routine.className || "",
    sectionName: routine.sectionName || "",
    roomNumber: routine.roomNumber || "",
    scheduledIn: routine.scheduledIn || "",
    scheduledOut: routine.scheduledOut || "",
    classStatus: "normal",
    suspensionReason: ""
  };
}

export async function saveRoutineLog({ routine, date, day, patch, user, settings }) {
  const logId = `${date}_${routine.id || routine.routineId}`;
  const ref = doc(db, "timeLogs", logId);
  const existingSnapshot = await getDoc(ref);
  const existing = existingSnapshot.exists() ? existingSnapshot.data() : {};
  const merged = stripUndefined({
    ...routineLogBase(routine, date, day),
    ...existing,
    ...patch,
    logId,
    updatedBy: user?.uid || user?.id || "",
    updatedByName: user?.name || user?.email || "",
    updatedAt: serverTimestamp(),
    createdAt: existing.createdAt || serverTimestamp()
  });
  const metrics = calculateLogMetrics(merged, settings);
  const userQueueStatus =
    merged.classStatus === "suspended" ? "suspended" : merged.actualIn && !merged.actualOut ? "pendingOut" : merged.actualOut ? "completed" : "notStarted";

  await setDoc(ref, { ...merged, ...metrics, userQueueStatus }, { merge: true });
  return logId;
}

export async function suspendRoutine({ routine, date, day, reason, user, settings }) {
  return saveRoutineLog({
    routine,
    date,
    day,
    user,
    settings,
    patch: {
      classStatus: "suspended",
      suspensionReason: reason,
      suspensionDate: date,
      suspensionTime: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    }
  });
}

export async function adminUpdateTimeLog(log, patch, settings, user) {
  const ref = doc(db, "timeLogs", log.id || log.logId);
  const merged = stripUndefined({
    ...log,
    ...patch,
    updatedBy: user?.uid || user?.id || "",
    updatedByName: user?.name || user?.email || "Admin",
    updatedAt: serverTimestamp()
  });
  delete merged.id;
  const metrics = calculateLogMetrics(merged, settings);
  const userQueueStatus =
    merged.classStatus === "suspended" ? "suspended" : merged.actualIn && !merged.actualOut ? "pendingOut" : merged.actualOut ? "completed" : "notStarted";
  await setDoc(ref, { ...merged, ...metrics, userQueueStatus }, { merge: true });
}

export async function deleteTimeLog(id) {
  await deleteDoc(doc(db, "timeLogs", id));
}

export async function bulkChangeRoutineRoom({ routines, sourceRoomId, targetRoom }) {
  const selected = routines.filter((routine) => routine.roomId === sourceRoomId);
  if (!selected.length) return 0;

  const batch = writeBatch(db);
  selected.forEach((routine) => {
    batch.update(doc(db, "routines", routine.id), {
      roomId: targetRoom.id,
      roomNumber: targetRoom.roomNumber,
      updatedAt: serverTimestamp()
    });
  });
  await batch.commit();
  return selected.length;
}

export async function getDashboardData(date = getTodayKey(), day = getDayName()) {
  const [routines, logs] = await Promise.all([getTodayRoutines(day), getTodayLogs(date)]);
  const summary = summarizeLogs(routines, logs);
  return { routines, logs, summary };
}

export async function getDashboardMetric(metric, date = getTodayKey(), day = getDayName()) {
  if (metric === "scheduled") {
    const routines = await getTodayRoutines(day);
    return routines.length;
  }

  const logs = await getTodayLogs(date);
  const routines = metric === "pendingOut" ? await getTodayRoutines(day) : [];
  const summary = summarizeLogs(routines, logs);
  return summary[metric] ?? 0;
}

export function summarizeLogs(routines = [], logs = []) {
  return {
    scheduled: routines.length,
    inMarked: logs.filter((log) => log.actualIn).length,
    outCompleted: logs.filter((log) => log.actualOut).length,
    pendingOut: logs.filter((log) => log.actualIn && !log.actualOut && log.classStatus !== "suspended").length,
    suspended: logs.filter((log) => log.classStatus === "suspended").length,
    lateEntries: logs.filter((log) => log.entryStatus === "Late Entry").length,
    earlyEntries: logs.filter((log) => log.entryStatus === "Early Entry").length,
    onTimeEntries: logs.filter((log) => log.entryStatus === "On Time Entry").length,
    lateEntryMinutes: logs.reduce((sum, log) => sum + Number(log.lateEntryMinutes || 0), 0),
    earlyEntryMinutes: logs.reduce((sum, log) => sum + Number(log.earlyEntryMinutes || 0), 0),
    lateExitMinutes: logs.reduce((sum, log) => sum + Number(log.lateExitMinutes || 0), 0),
    earlyExitMinutes: logs.reduce((sum, log) => sum + Number(log.earlyExitMinutes || 0), 0)
  };
}

function buildLogQuery(filters = {}, extraConstraints = []) {
  const constraints = [];
  if (filters.from) constraints.push(where("date", ">=", filters.from));
  if (filters.to) constraints.push(where("date", "<=", filters.to));
  if (filters.teacherId) constraints.push(where("teacherId", "==", filters.teacherId));
  if (filters.classId) constraints.push(where("classId", "==", filters.classId));
  if (filters.subjectId) constraints.push(where("subjectId", "==", filters.subjectId));
  if (filters.roomId) constraints.push(where("roomId", "==", filters.roomId));
  if (filters.classStatus) constraints.push(where("classStatus", "==", filters.classStatus));
  return query(collection(db, "timeLogs"), ...constraints, orderBy("date", "desc"), ...extraConstraints);
}

export async function getTimeLogsPage(filters = {}, pageSize = 50, cursor = null) {
  const constraints = [limit(pageSize)];
  if (cursor) constraints.unshift(startAfter(cursor));
  const snapshot = await getDocs(buildLogQuery(filters, constraints));
  return {
    rows: snapRows(snapshot),
    cursor: snapshot.docs[snapshot.docs.length - 1] || null,
    hasMore: snapshot.size === pageSize
  };
}

export async function getReportLogs(filters = {}) {
  const snapshot = await getDocs(buildLogQuery(filters));
  return snapRows(snapshot);
}

export async function seedSampleMasters() {
  const master = await getMasterData(true);
  if (master.teachers.length || master.subjects.length || master.classes.length || master.rooms.length) {
    return false;
  }

  const subjectRef = doc(collection(db, "subjects"));
  const teacherRef = doc(collection(db, "teachers"));
  const classRef = doc(collection(db, "classes"));
  const sectionRef = doc(collection(db, "sections"));
  const roomRef = doc(collection(db, "rooms"));
  const routineRef = doc(collection(db, "routines"));
  const batch = writeBatch(db);

  batch.set(subjectRef, {
    subjectId: subjectRef.id,
    name: "Biology",
    color: "#22c55e",
    icon: "🧬",
    active: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  batch.set(teacherRef, {
    teacherId: teacherRef.id,
    name: "Mr. ABC",
    subjectIds: [subjectRef.id],
    subject: "Biology",
    active: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  batch.set(classRef, {
    classId: classRef.id,
    name: "Class 11",
    active: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  batch.set(sectionRef, {
    sectionId: sectionRef.id,
    classId: classRef.id,
    name: "Section B",
    active: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  batch.set(roomRef, {
    roomId: roomRef.id,
    roomNumber: "204",
    active: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  batch.set(routineRef, {
    routineId: routineRef.id,
    days: [getDayName()],
    classId: classRef.id,
    sectionId: sectionRef.id,
    roomId: roomRef.id,
    subjectId: subjectRef.id,
    teacherId: teacherRef.id,
    className: "Class 11",
    sectionName: "Section B",
    roomNumber: "204",
    subjectName: "Biology",
    teacherName: "Mr. ABC",
    scheduledIn: "07:00",
    scheduledOut: "08:00",
    status: "active",
    active: true,
    order: 1,
    notes: "Sample routine",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  await batch.commit();
  clearMasterCache();
  return true;
}
